/**
 * Política de notificação do OPERADOR por-tenant — Frente N, sub-frente N3a
 * (quiet-time / escala de plantão; conformidade trabalhista por construção:
 * direito à desconexão + sobreaviso CLT art. 244 §2º).
 *
 * Campo IRMÃO de `Tenant.proactiveCommunicationPolicy` (INV-N3): aquela governa
 * o CLIENTE (Person — quiet-hours/opt-out de comunicação proativa); esta governa
 * o OPERADOR (User — quando o push do trabalho PODE tocar o celular pessoal).
 * Semânticas ortogonais → campos distintos.
 *
 * **Régua código×dado (MAC §0):** a config é DADO (janelas de expediente, fuso,
 * sem deploy). O FILTRO determinístico que SUPRIME o push fora do regime é CÓDIGO
 * (a garantia trabalhista: o push nem sai do servidor no horário errado). As
 * funções puras abaixo são a fronteira testável dessa garantia (juiz-independente,
 * sem DB/relógio embutido) — molde `proactive-communication-policy.ts`.
 *
 * Contrato fail-safe (P5/P12 — opt-in real): `raw` null/ausente/malformado ⇒
 * `enabled:false` = comportamento N1/B7 byte-a-byte (inerte, nunca suprime).
 *
 * O consumo (regimes 1/2/3, piso `never-suppress`, plantão) vive no
 * `OperatorPushGuardService` do Engine — aqui só a policy + o cômputo de regime.
 */
const DEFAULT_TIMEZONE = 'America/Sao_Paulo';
const MINUTES_IN_DAY = 1440;
function asObject(v) {
    return v !== null && typeof v === 'object' && !Array.isArray(v)
        ? v
        : null;
}
function isValidTimeZone(tz) {
    if (typeof tz !== 'string' || tz.length === 0)
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
function isMinute(n, min, max) {
    return typeof n === 'number' && Number.isInteger(n) && n >= min && n <= max;
}
function isDayOfWeek(n) {
    return typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 6;
}
/**
 * Valida/normaliza uma janela crua. Descarta (retorna null) se: dias não-array,
 * dias vazios ou fora de 0-6, startMinute fora de [0,1439], endMinute fora de
 * [1,1440], ou extensão zero (start === end — ambígua). Dedup de dias.
 */
function normalizeWindow(raw) {
    const obj = asObject(raw);
    if (obj === null)
        return null;
    if (!Array.isArray(obj.daysOfWeek))
        return null;
    const days = Array.from(new Set(obj.daysOfWeek.filter(isDayOfWeek))).sort((a, b) => a - b);
    if (days.length === 0)
        return null;
    if (!isMinute(obj.startMinute, 0, MINUTES_IN_DAY - 1))
        return null;
    if (!isMinute(obj.endMinute, 1, MINUTES_IN_DAY))
        return null;
    if (obj.startMinute === obj.endMinute)
        return null;
    return { daysOfWeek: days, startMinute: obj.startMinute, endMinute: obj.endMinute };
}
/**
 * Resolve a política efetiva a partir do Json cru do Tenant
 * (`Tenant.operatorNotificationPolicy`). Fail-safe: qualquer coisa malformada
 * cai no default inerte (`enabled:false`).
 */
export function resolveOperatorNotificationPolicy(raw) {
    const defaults = {
        version: 1,
        enabled: false,
        timeZone: DEFAULT_TIMEZONE,
        shiftWindows: [],
        operatorCanSelfMarkOnCall: false,
    };
    const obj = asObject(raw);
    if (obj === null)
        return defaults;
    const enabled = obj.enabled === true;
    const timeZone = isValidTimeZone(obj.timeZone) ? obj.timeZone : DEFAULT_TIMEZONE;
    const shiftWindows = Array.isArray(obj.shiftWindows)
        ? obj.shiftWindows.map(normalizeWindow).filter((w) => w !== null)
        : [];
    const operatorCanSelfMarkOnCall = obj.operatorCanSelfMarkOnCall === true;
    return { version: 1, enabled, timeZone, shiftWindows, operatorCanSelfMarkOnCall };
}
const WEEKDAY_INDEX = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
};
/** Dia-da-semana (0-6) e minuto-do-dia (0-1439) de um instante num fuso IANA, via Intl. */
function localParts(at, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
        // `h23` força 00-23 (evita `24` de meia-noite em alguns ICU, que traria o
        // weekday do dia que TERMINA — classificaria o instante no dia errado).
        hourCycle: 'h23',
    }).formatToParts(at);
    let weekday = 'Sunday';
    let hour = 0;
    let minute = 0;
    for (const p of parts) {
        if (p.type === 'weekday')
            weekday = p.value;
        else if (p.type === 'hour')
            hour = Number(p.value) === 24 ? 0 : Number(p.value);
        else if (p.type === 'minute')
            minute = Number(p.value);
    }
    return { day: WEEKDAY_INDEX[weekday] ?? 0, minute: hour * 60 + minute };
}
/** A janela cobre (day, minute)? Trata cross-midnight como spillover para o dia seguinte. */
function windowCovers(w, day, minute) {
    if (w.startMinute < w.endMinute) {
        // Mesmo dia: [start, end).
        return w.daysOfWeek.includes(day) && minute >= w.startMinute && minute < w.endMinute;
    }
    // Cruza a meia-noite: [start, 1440) no dia da janela ∪ [0, end) no dia SEGUINTE.
    const prevDay = (day + 6) % 7;
    return ((w.daysOfWeek.includes(day) && minute >= w.startMinute) ||
        (w.daysOfWeek.includes(prevDay) && minute < w.endMinute));
}
/**
 * Regime do operador em `now` sob a `policy`: `'on-shift'` (expediente — a matriz
 * de atenção N1 vale cheia) ou `'off-shift'` (fora do expediente — o gate suprime
 * o push, salvo compliance/plantão-urgent, decididos no Engine). Policy desabilitada
 * ⇒ SEMPRE `'on-shift'` (inerte). Puro (só `Intl`, sem DB/relógio embutido).
 */
export function computeOperatorRegime(policy, now) {
    if (!policy.enabled)
        return 'on-shift';
    const { day, minute } = localParts(now, policy.timeZone);
    return policy.shiftWindows.some((w) => windowCovers(w, day, minute)) ? 'on-shift' : 'off-shift';
}
//# sourceMappingURL=operator-notification-policy.js.map